import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import { MembershipRole, isEffectiveAdmin } from '../lib/groups';
import LoadingScreen from './LoadingScreen';

interface Props {
  allow: MembershipRole[];
  children: React.ReactElement;
}

interface RoleLike {
  role: MembershipRole;
  is_admin: boolean;
}

// Admin access no longer implies a big/little can't also hold it — a member
// "has" a given allowed role if it's their literal role, or if 'admin' is
// being asked for and they carry the is_admin flag (whatever their role).
const hasAnyRole = (membership: RoleLike, allow: MembershipRole[]) =>
  allow.includes(membership.role) || (allow.includes('admin') && isEffectiveAdmin(membership));

export const homeForRole = (membership: RoleLike) =>
  isEffectiveAdmin(membership) ? '/group/approvals' : '/group/submit-ranking';

// Gates a /group/* page behind: signed in, has an approved membership,
// and their role is one of `allow`. Anything short of that redirects to
// wherever they actually belong.
const RequireGroupRole = ({ allow, children }: Props) => {
  const { user, loading: authLoading } = useAuth();
  const { membership, initialized } = useGroup();

  if (authLoading || !initialized) {
    return <LoadingScreen />;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!membership) return <Navigate to="/group/onboarding" replace />;
  if (membership.status !== 'approved') return <Navigate to="/group/pending" replace />;
  if (!hasAnyRole(membership, allow)) return <Navigate to={homeForRole(membership)} replace />;

  return children;
};

export default RequireGroupRole;
